import { memo, useMemo } from "react";
import Chart from "react-apexcharts";

import "./OperatorProductionMixChart.scss";

const safeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const formatQuantity = (value) =>
  safeNumber(value).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  });

function OperatorProductionMixChart({
  series = [],
  totalProduced = 0,
  overallQuality = 0,
  chartKey = "default",
  loading = false,
}) {
  const safeSeries = useMemo(
    () => [
      safeNumber(series?.[0]),
      safeNumber(series?.[1]),
      safeNumber(series?.[2]),
    ],
    [series],
  );

  const safeTotalProduced = safeNumber(totalProduced);
  const safeOverallQuality = safeNumber(overallQuality);

  const hasData = safeSeries.some((value) => value > 0);

  const animationKey = useMemo(
    () =>
      [
        chartKey,
        safeSeries.join("-"),
        safeTotalProduced,
        safeOverallQuality,
      ].join("_"),
    [
      chartKey,
      safeSeries,
      safeTotalProduced,
      safeOverallQuality,
    ],
  );

  const options = useMemo(
    () => ({
      chart: {
        id: `operator-production-mix-${chartKey}`,
        type: "donut",

        toolbar: {
          show: false,
        },

        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 1100,

          animateGradually: {
            enabled: true,
            delay: 180,
          },

          dynamicAnimation: {
            enabled: true,
            speed: 700,
          },
        },

        redrawOnParentResize: false,
        redrawOnWindowResize: false,
      },

      labels: ["OK", "Reject", "Rework"],

      colors: ["#10b981", "#ef4444", "#f59e0b"],

      stroke: {
        width: 4,
        colors: ["#ffffff"],
        lineCap: "round",
      },

      fill: {
        type: "gradient",

        gradient: {
          shade: "light",
          shadeIntensity: 0.2,
          inverseColors: false,
          opacityFrom: 1,
          opacityTo: 0.88,
          stops: [0, 90, 100],
        },
      },

      dataLabels: {
        enabled: false,
      },

      legend: {
        position: "bottom",
        fontSize: "11px",
        fontWeight: 600,
        horizontalAlign: "center",
        itemMargin: {
          horizontal: 10,
          vertical: 6,
        },
        markers: {
          width: 9,
          height: 9,
          radius: 9,
        },
        formatter: (seriesName, opts) => {
          const value =
            opts?.w?.globals?.series?.[
              opts.seriesIndex
            ] ?? 0;

          return `${seriesName}: ${formatQuantity(value)}`;
        },
      },

      plotOptions: {
        pie: {
          expandOnClick: true,
          startAngle: -90,
          endAngle: 270,
          customScale: 0.95,

          donut: {
            size: "73%",

            labels: {
              show: true,

              name: {
                show: true,
                offsetY: -7,
                color: "#64748b",
                fontSize: "11px",
                fontWeight: 600,
              },

              value: {
                show: true,
                offsetY: 6,
                color: "#0f172a",
                fontSize: "24px",
                fontWeight: 800,
                formatter: formatQuantity,
              },

              total: {
                show: true,
                showAlways: true,
                label: "Total Produced",
                color: "#64748b",
                fontSize: "11px",
                fontWeight: 600,

                formatter: () =>
                  formatQuantity(safeTotalProduced),
              },
            },
          },
        },
      },

      states: {
        normal: {
          filter: {
            type: "none",
          },
        },

        hover: {
          filter: {
            type: "lighten",
            value: 0.08,
          },
        },

        active: {
          allowMultipleDataPointsSelection: false,

          filter: {
            type: "darken",
            value: 0.08,
          },
        },
      },

      tooltip: {
        enabled: true,
        fillSeriesColor: false,

        y: {
          formatter: (value) =>
            `${formatQuantity(value)} units`,
        },
      },

      noData: {
        text: "No production mix data",
        align: "center",
        verticalAlign: "middle",

        style: {
          color: "#94a3b8",
          fontSize: "12px",
        },
      },
    }),
    [chartKey, safeTotalProduced],
  );

  if (loading) {
    return (
      <div className="operator-production-mix-chart operator-production-mix-chart--loading">
        <div className="operator-production-mix-chart__skeleton">
          <div className="operator-mix-skeleton-donut">
            <div className="operator-mix-skeleton-center" />
          </div>

          <div className="operator-mix-skeleton-legend">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={`mix-skeleton-${index}`}
                className="operator-mix-skeleton-legend-item"
                style={{
                  animationDelay: `${index * 120}ms`,
                }}
              >
                <span />
                <i />
              </div>
            ))}
          </div>
        </div>

        <div className="operator-production-mix-chart__quality operator-production-mix-chart__quality--loading">
          <span className="operator-mix-quality-skeleton" />
          <strong className="operator-mix-value-skeleton" />
        </div>
      </div>
    );
  }

  return (
    <div
      key={animationKey}
      className="operator-production-mix-chart operator-production-mix-chart--visible"
    >
      {hasData ? (
        <div className="operator-production-mix-chart__chart">
          <Chart
            options={options}
            series={safeSeries}
            type="donut"
            height={300}
          />
        </div>
      ) : (
        <div className="operator-production-mix-chart__empty">
          <span className="operator-production-mix-chart__empty-icon">
            ◔
          </span>

          <span>No production mix data</span>
        </div>
      )}

      <div className="operator-production-mix-chart__quality">
        <span>Overall quality</span>

        <strong>
          {safeOverallQuality.toFixed(2)}%
        </strong>
      </div>
    </div>
  );
}

export default memo(OperatorProductionMixChart);